import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../context/AuthContext';

// ── THE LIVE CREDIT BALANCE ──────────────────────────────────────────────────
// A SUBSCRIPTION, not a read. AuthContext's `profile` is fetched once when the
// session starts, which is right for a name and an avatar and wrong for a
// number that changes every time the user renders something: the header would
// still show 4 after a generation had spent one, until a full page reload.
//
// The server is the only writer (functions/credits.js debits inside a
// transaction, and firestore.rules refuses the field to the client), so this
// listener is the whole synchronisation story — a successful generation, a
// refund after a provider failure, and a future top-up all arrive the same way,
// with no client-side arithmetic to drift out of step and nothing to invalidate.
//
// Its own listener rather than an addition to AuthContext: auth behaviour was
// explicitly not to be changed, and this stays out of that path entirely.
export default function useCredits() {
  const { user } = useAuth();
  const [credits, setCredits] = useState(null);   // null = not known yet
  const [plan, setPlan] = useState('free');

  useEffect(() => {
    if (!user?.uid) { setCredits(null); setPlan('free'); return undefined; }
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        setCredits(Number(snap.get('credits')) || 0);
        setPlan(snap.get('plan') || 'free');
      },
      // Offline, or rules refusing the read. Leaving the last known value on
      // screen beats replacing it with a zero the account does not actually
      // have — the balance that matters is the server's, and it is re-read the
      // moment the connection returns.
      () => {},
    );
    return unsub;
  }, [user?.uid]);

  return { credits, plan, known: credits !== null };
}
